import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoItemResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * All sub-tasks of a shared list in one read, grouped by their parent item id so
 * the List view can render each task's sub-tasks nested beneath it. Keyed under
 * the list detail, so any item mutation (create/complete/delete a sub-task)
 * refreshes it alongside the top-level items.
 */
export function useTodoListSubtasks(holderId: string | null, listId: string) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.todoLists.subtasksAll(holderId ?? '', listId),
    queryFn: () => api.listTodoListSubtasks(holderId as string, listId),
    enabled: !!holderId && !!listId,
  });

  const byParent = useMemo(() => {
    const map = new Map<string, TodoItemResponse[]>();
    for (const st of data?.subtasks ?? []) {
      if (!st.parentItemId) continue;
      const arr = map.get(st.parentItemId);
      if (arr) arr.push(st);
      else map.set(st.parentItemId, [st]);
    }
    return map;
  }, [data]);

  return { subtasksByParent: byParent, loading: isLoading };
}
